import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Component } from 'react';
import Select, {
  components,
  createFilter,
  DropdownIndicatorProps,
  IndicatorSeparatorProps,
  NoticeProps,
  PlaceholderProps,
  Props,
} from 'react-select';
import {
  IndicatorsContainerProps,
  ValueContainerProps,
} from 'react-select/dist/declarations/src/components/containers';
import { ControlProps } from 'react-select/dist/declarations/src/components/Control';
import { GroupHeadingProps, GroupProps } from 'react-select/dist/declarations/src/components/Group';
import { InputProps } from 'react-select/dist/declarations/src/components/Input';
import { MenuListProps, MenuProps } from 'react-select/dist/declarations/src/components/Menu';
import { OptionProps } from 'react-select/dist/declarations/src/components/Option';
import { SingleValueProps } from 'react-select/dist/declarations/src/components/SingleValue';

import css from './CustomSelect.css';

const ControlComponent = ({ children, innerProps, innerRef, isFocused }: ControlProps) => (
  <div
    {...innerProps}
    ref={innerRef}
    className={classNames(css.ControlComponent, {
      [css.focused]: isFocused,
    })}
  >
    {children}
  </div>
);

const ValueContainerComponent = ({ children, innerProps }: ValueContainerProps) => (
  <div {...innerProps} className={css.ValueContainerComponent}>
    {children}
  </div>
);

const SingleValueComponent = ({ children, innerProps }: SingleValueProps) => (
  <div {...innerProps} className={css.SingleValueComponent}>
    {children}
  </div>
);

const IndicatorsContainerComponent = ({ children, innerProps }: IndicatorsContainerProps) => (
  <div {...innerProps} className={css.IndicatorsContainerComponent}>
    {children}
  </div>
);

const IndicatorSeparatorComponent = ({ innerProps }: IndicatorSeparatorProps) => (
  <span {...innerProps} className={css.IndicatorSeparatorComponent} />
);

const MenuComponent = ({ children, innerProps, innerRef }: MenuProps) => (
  <div {...innerProps} ref={innerRef} className={css.MenuComponent}>
    {children}
  </div>
);

const OptionComponent = ({ children, innerProps, innerRef, isFocused }: OptionProps) => (
  <div
    {...innerProps}
    ref={innerRef}
    className={classNames(css.OptionComponent, {
      [css.focused]: isFocused,
    })}
  >
    {children}
  </div>
);

const InputComponent = (props: InputProps) => <components.Input {...props} className={css.InputComponent} />;

const NoOptionsMessageComponent = ({ children, ...props }: NoticeProps) => (
  <components.NoOptionsMessage {...props} className={css.NoOptionsMessageComponent}>
    {children}
  </components.NoOptionsMessage>
);

const GroupComponent = ({ children, ...props }: GroupProps) => (
  <components.Group {...props} className={css.GroupComponent}>
    {children}
  </components.Group>
);

const GroupHeadingComponent = (props: GroupHeadingProps) => {
  return <components.GroupHeading {...props} className={css.GroupHeadingComponent} />;
};

const DropdownIndicatorComponent = ({ className, ...props }: DropdownIndicatorProps) => (
  <components.DropdownIndicator {...props} className={classNames(className, css.DropdownIndicatorComponent)} />
);

const PlaceholderComponent = ({ children, className, ...props }: PlaceholderProps) => (
  <components.Placeholder {...props} className={classNames(css.PlaceholderComponent, className)}>
    {children}
  </components.Placeholder>
);

export const MenuListComponent = ({ children, className, ...props }: MenuListProps) => (
  <components.MenuList {...props} className={classNames(css.MenuListComponent, className)}>
    {children}
  </components.MenuList>
);

type CustomSelectProps = Props;

@observer
class CustomSelect extends Component<CustomSelectProps> {
  render() {
    const { className, components: customComponents, ...props } = this.props;

    return (
      <Select
        className={classNames(css.container, className)}
        filterOption={createFilter({ matchFrom: 'start' })} // Search by label
        blurInputOnSelect
        components={{
          Control: ControlComponent,
          ValueContainer: ValueContainerComponent,
          SingleValue: SingleValueComponent,
          IndicatorsContainer: IndicatorsContainerComponent,
          IndicatorSeparator: IndicatorSeparatorComponent,
          Menu: MenuComponent,
          MenuList: MenuListComponent,
          Option: OptionComponent,
          Input: InputComponent,
          NoOptionsMessage: NoOptionsMessageComponent,
          Group: GroupComponent,
          GroupHeading: GroupHeadingComponent,
          DropdownIndicator: DropdownIndicatorComponent,
          Placeholder: PlaceholderComponent,
          ...customComponents,
        }}
        {...props}
      />
    );
  }
}

export default CustomSelect;
